import {
  Bot,
  LogOut,
  Mail,
  MessageSquareText,
  Search,
  UserRound,
  UsersRound,
  X,
} from "lucide-react";

import type { Conversation, Mode, User } from "../types";
import {
  contactName,
  formatListTime,
  initials,
  messagePreview,
} from "../utils";
import NotificationBell from "./NotificationBell";
import PwaControls from "./PwaControls";


type ModeFilter = "" | Mode;

interface Props {
  conversations: Conversation[];
  selectedId: number | null;
  loading: boolean;
  search: string;
  modeFilter: ModeFilter;
  unreadOnly: boolean;
  user: User;
  token: string;
  onSearchChange: (value: string) => void;
  onModeFilterChange: (value: ModeFilter) => void;
  onUnreadOnlyChange: (value: boolean) => void;
  onSelect: (conversation: Conversation) => void;
  onLogout: () => void;
}

const filters: Array<{
  value: ModeFilter;
  label: string;
  icon: typeof MessageSquareText;
}> = [
  { value: "", label: "Tutte", icon: MessageSquareText },
  { value: "human", label: "Umane", icon: UsersRound },
  { value: "ai", label: "AI", icon: Bot },
];

export default function ConversationList({
  conversations,
  selectedId,
  loading,
  search,
  modeFilter,
  unreadOnly,
  user,
  token,
  onSearchChange,
  onModeFilterChange,
  onUnreadOnlyChange,
  onSelect,
  onLogout,
}: Props) {
  return (
    <aside className={`conversation-sidebar ${selectedId ? "has-mobile-selection" : ""}`}>
      <header className="sidebar-header">
        <div className="sidebar-brand">
          <img
            className="brand-logo sidebar-logo"
            src="/gdp-logo.png"
            alt=""
          />
          <div>
            <strong>GDP WhatsApp</strong>
            <span>WhatsApp Business</span>
          </div>
        </div>
        <div className="header-actions">
          <NotificationBell token={token} />
          <div className="live-indicator">
            <span />
            Live
          </div>
        </div>
      </header>

      <div className="conversation-tools">
        <div className="search-box">
          <Search size={17} aria-hidden="true" />
          <input
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Cerca nome o numero"
            aria-label="Cerca conversazioni"
          />
          {search && (
            <button
              className="icon-button tiny"
              onClick={() => onSearchChange("")}
              aria-label="Cancella ricerca"
            >
              <X size={15} />
            </button>
          )}
        </div>
        <div className="filter-tabs" aria-label="Filtra conversazioni">
          {filters.map(({ value, label, icon: Icon }) => (
            <button
              key={label}
              className={
                modeFilter === value && !(value === "" && unreadOnly)
                  ? "active"
                  : ""
              }
              onClick={() => onModeFilterChange(value)}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
          <button
            className={unreadOnly ? "active" : ""}
            onClick={() => onUnreadOnlyChange(!unreadOnly)}
            aria-pressed={unreadOnly}
          >
            <Mail size={14} />
            Non lette
          </button>
        </div>
      </div>

      <div className="conversation-list" aria-live="polite">
        {loading && conversations.length === 0 && (
          <div className="conversation-skeletons" aria-label="Caricamento">
            {[0, 1, 2, 3, 4].map((item) => (
              <div className="conversation-skeleton" key={item}>
                <span />
                <div><i /><i /></div>
              </div>
            ))}
          </div>
        )}

        {!loading && conversations.length === 0 && (
          <div className="empty-list">
            <MessageSquareText size={30} />
            <strong>
              {unreadOnly ? "Nessuna chat non letta" : "Nessuna conversazione"}
            </strong>
            <p>
              {unreadOnly
                ? "Tutti i messaggi risultano letti."
                : "Prova a modificare ricerca o filtro."}
            </p>
          </div>
        )}

        {conversations.map((conversation) => {
          const name = contactName(conversation);
          return (
            <button
              className={[
                "conversation-row",
                selectedId === conversation.id ? "active" : "",
                conversation.has_unread ? "unread" : "",
              ].filter(Boolean).join(" ")}
              key={conversation.id}
              onClick={() => onSelect(conversation)}
            >
              <div className="contact-avatar">{initials(name)}</div>
              <div className="conversation-row-main">
                <div className="conversation-row-top">
                  <strong>{name}</strong>
                  <div className="conversation-row-meta">
                    <time>{formatListTime(conversation.last_message_at)}</time>
                    {conversation.has_unread && (
                      <span
                        className="unread-dot"
                        title="Messaggi non letti"
                        aria-label="Messaggi non letti"
                      />
                    )}
                  </div>
                </div>
                <div className="conversation-row-bottom">
                  <span>{messagePreview(conversation)}</span>
                  <i className={`mode-dot ${conversation.mode}`} title={conversation.mode === "human" ? "Gestione umana" : "Gestione AI"}>
                    {conversation.mode === "human" ? <UserRound size={11} /> : <Bot size={11} />}
                  </i>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <PwaControls />

      <footer className="sidebar-footer">
        <div className="user-avatar">{initials(user.display_name)}</div>
        <div className="sidebar-user">
          <strong>{user.display_name}</strong>
          <span>Operatore</span>
        </div>
        <button className="icon-button" onClick={onLogout} title="Esci" aria-label="Esci">
          <LogOut size={18} />
        </button>
      </footer>
    </aside>
  );
}
